import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  SxProps,
} from "@mui/material";
import { Cancel, Delete, Edit, Save } from "@mui/icons-material";
import ChallengeTeamSelector from "./ChallengeTeamSelector";
import InterruptingChallengeSelector from "./InterruptingChallengeSelector";
import Grid from "@mui/material/Grid";
import SetSelector from "./SetSelector";
import ScoreSelector from "./ScoreSelector";
import ChallengeTypeSelector from "./ChallengeTypeSelector";
import ChallengeResultSelector from "./ChallengeResultSelector";
import { useSelectedChallengeContext } from "@context/SelectedChallengeContext";
import { useGameDataContext } from "@context/GameDataContext";
import { Challenge, TeamReference } from "@/models/pdfData";
import { formatScore } from "@context/utils";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { gameState } from "@/services/gameState";

const EditChallenge: FC = () => {
  const {
    challenge,
    setScoreRallyStart,
    setScoreRallyEnd,
    setScoreAfterChallenge,
    teamInterruptedGame,
    newChallenge,
  } = useSelectedChallengeContext();
  const { game, addChallenge, deleteChallenge } = useGameDataContext();
  const currentScore = gameState("currentScore");
  const getCurrentScoreForTeam = gameState("getCurrentScoreForTeam");
  const { onMobile, onTablet } = useResponsiveContext();

  const [activeStep, setActiveStep] = useState(0);
  const [isInterruptingChallenge, setInterruptingChallenge] = useState(
    challenge.team === "referee" || teamInterruptedGame,
  );
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [showNextButton, setShowNextButton] = useState(true);

  useEffect(() => {
    setInterruptingChallenge(
      challenge.team === "referee" || teamInterruptedGame,
    );
  }, [teamInterruptedGame, challenge]);

  useEffect(() => {
    if (
      isInterruptingChallenge &&
      !challenge?.scoreRallyStart &&
      currentScore
    ) {
      setScoreRallyStart(currentScore);
    }
    if (!isInterruptingChallenge) {
      if (challenge?.scoreRallyEnd) {
        setScoreRallyStart({
          team1:
            challenge.scoreRallyEnd?.team1 -
            (challenge?.team === "team2" ? 1 : 0),
          team2:
            challenge.scoreRallyEnd?.team2 -
            (challenge?.team === "team1" ? 1 : 0),
        });
      }
    }
    if (challenge?.scoreRallyStart && challenge?.challengeResult) {
      const { scoreRallyStart, challengeResult, team } = challenge;
      if (!isInterruptingChallenge && challenge?.scoreRallyEnd) {
        const { scoreRallyEnd } = challenge;
        if (challengeResult === team) {
          if (challenge.typeOfChallenge?.startsWith("no-")) {
            setScoreAfterChallenge({
              team1:
                scoreRallyEnd?.team1 - (challenge?.team === "team1" ? 0 : 1),
              team2:
                scoreRallyEnd?.team2 - (challenge?.team === "team2" ? 0 : 1),
            });
          } else {
            setScoreAfterChallenge({
              team1:
                scoreRallyEnd?.team1 + (challenge?.team === "team1" ? 1 : -1),
              team2:
                scoreRallyEnd?.team2 + (challenge?.team === "team2" ? 1 : -1),
            });
          }
        } else {
          setScoreAfterChallenge({
            team1: scoreRallyEnd?.team1,
            team2: scoreRallyEnd?.team2,
          });
        }
      }
      if (isInterruptingChallenge && scoreRallyStart) {
        if (challengeResult === "unresolvable" && teamInterruptedGame) {
          setScoreAfterChallenge({
            team1:
              scoreRallyStart?.team1 + (challenge.team === "team2" ? 1 : 0),
            team2:
              scoreRallyStart?.team2 + (challenge.team === "team1" ? 1 : 0),
          });
        } else {
          setScoreAfterChallenge({
            team1:
              scoreRallyStart?.team1 + (challengeResult === "team1" ? 1 : 0),
            team2:
              scoreRallyStart?.team2 + (challengeResult === "team2" ? 1 : 0),
          });
        }
      }
    }
    if (challenge && challenge.challengeResult && challenge.team) {
      secondStep(false);
    }
  }, [currentScore, challenge, isInterruptingChallenge]);

  useEffect(() => {
    const someScoreIsSet =
      challenge?.scoreRallyStart || challenge?.scoreRallyEnd;
    const scoreHasChanged =
      JSON.stringify(currentScore) !==
      JSON.stringify(
        isInterruptingChallenge
          ? challenge?.scoreRallyStart
          : challenge?.scoreRallyEnd,
      );
    setShowNextButton(!challenge?.challengeResult);
    setShowRefreshButton(
      !!(
        someScoreIsSet &&
        currentScore &&
        scoreHasChanged &&
        !challenge?.challengeResult
      ),
    );
  }, [challenge, currentScore]);

  const hasRelevantScore = () =>
    isInterruptingChallenge
      ? !!challenge?.scoreRallyStart
      : !!challenge?.scoreRallyEnd;

  const firstStep = () => setActiveStep(0);
  const secondStep = (useNewScore: boolean = false) => {
    if (currentScore && (useNewScore || !hasRelevantScore())) {
      if (isInterruptingChallenge) {
        setScoreRallyStart(currentScore);
      } else {
        setScoreRallyEnd(currentScore);
      }
    }
    setActiveStep(1);
  };

  const saveChallenge = () => {
    const missingInputs = Object.entries(challenge)
      .filter(([key, value]) => !value && key != "scoreRallyEnd" && key != "id")
      .map(([key]) => key);
    if (missingInputs.length <= 0 && game) {
      addChallenge(game.samsId, challenge as Challenge);
      resetChallenge();
    } else {
      console.debug(missingInputs);
    }
  };

  const removeChallenge = () => {
    if (challenge?.id) {
      deleteChallenge(challenge.id);
      resetChallenge();
    }
  };

  const resetChallenge = () => {
    firstStep();
    newChallenge();
  };

  const centeredGridSx: SxProps = {
    display: "flex",
    justifyContent: "center",
  };

  return (
    <Box
      maxHeight={"100%"}
      height={"100%"}
      boxSizing={"border-box"}
      pt={onTablet ? 0 : 3}
      pb={onMobile ? 0 : 1}
      px={onMobile ? 0 : 2}
    >
      <Stepper
        sx={{
          overflow: "auto",
          maxHeight: "100%",
          paddingBottom: "8px",
        }}
        orientation="vertical"
        activeStep={activeStep}
      >
        <Step
          key={0}
          sx={{
            paddingTop: "8px",
          }}
        >
          <StepLabel
            optional={
              challenge?.team && activeStep !== 0 ? (
                <Button
                  variant="text"
                  startIcon={<Edit />}
                  onClick={() => firstStep()}
                  sx={{
                    textTransform: "none",
                    color: "inherit",
                    fontWeight: "inherit",
                  }}
                >
                  Challenge{" "}
                  {challenge.team === "referee"
                    ? "Referee"
                    : game?.gameData[challenge.team].longName ||
                      challenge.team}{" "}
                  (
                  {isInterruptingChallenge
                    ? "Spielzug unterbrochen"
                    : "nach Spielzugende"}
                  )
                </Button>
              ) : (
                " "
              )
            }
          >
            Challenge Antrag
          </StepLabel>
          <StepContent>
            <Box
              display={"flex"}
              justifyContent={"center"}
              gap={5}
              py={4}
              flexWrap={"wrap"}
            >
              <ChallengeTeamSelector />
              <InterruptingChallengeSelector />
            </Box>
            <Box display={"flex"} gap={3}>
              {showNextButton && (
                <Button
                  variant={showRefreshButton ? "outlined" : "contained"}
                  onClick={() => secondStep(false)}
                  disabled={!challenge?.team}
                  color={"primary"}
                >
                  {`Weiter`}
                  {(isInterruptingChallenge
                    ? challenge?.scoreRallyStart
                    : challenge?.scoreRallyEnd) &&
                    ` (mit Score ${formatScore(
                      isInterruptingChallenge
                        ? challenge.scoreRallyStart!
                        : challenge.scoreRallyEnd!,
                      challenge?.team,
                    )})`}
                </Button>
              )}
              {showRefreshButton ? (
                <Button
                  variant={"contained"}
                  onClick={() => secondStep(true)}
                  disabled={!challenge?.team}
                  color={"primary"}
                >
                  {`Weiter (mit neuem Score ${getCurrentScoreForTeam(challenge?.team as TeamReference)})`}
                </Button>
              ) : null}
            </Box>
          </StepContent>
        </Step>
        <Step key={1}>
          <StepLabel>Challenge</StepLabel>
          <StepContent>
            <Grid container columns={{ xs: 8, md: 8, lg: 8 }} rowGap={3}>
              <Grid size={{ xs: 8, md: 8, lg: 2 }} sx={centeredGridSx}>
                <SetSelector />
              </Grid>
              <Grid size={{ xs: 8, md: 4, lg: 3 }} sx={centeredGridSx}>
                <ScoreSelector
                  disabled={!isInterruptingChallenge}
                  score={challenge?.scoreRallyStart}
                  setScore={setScoreRallyStart}
                  title={"Punktestand Anpfiff"}
                />
              </Grid>
              <Grid size={{ xs: 8, md: 4, lg: 3 }} sx={centeredGridSx}>
                <ScoreSelector
                  greyedOut={isInterruptingChallenge}
                  score={challenge?.scoreRallyEnd}
                  setScore={setScoreRallyEnd}
                  title={"Punktestand Abpfiff"}
                />
              </Grid>
              <Grid size={8}>
                <Divider />
              </Grid>
              <Grid size={{ xs: 8, md: 4, lg: 5 }} sx={centeredGridSx}>
                <ChallengeTypeSelector />
              </Grid>
              <Grid size={{ xs: 8, md: 4, lg: 3 }} sx={centeredGridSx}>
                <ScoreSelector
                  disabled={true}
                  score={challenge?.scoreAfterChallenge}
                  setScore={setScoreAfterChallenge}
                  title={"Punktestand nach Challenge"}
                />
              </Grid>
              <Grid size={8} sx={centeredGridSx}>
                <ChallengeResultSelector />
              </Grid>
            </Grid>
            <Box display={"flex"} gap={3} justifyContent={"center"}>
              <Button
                sx={{ mt: 3 }}
                variant={"contained"}
                onClick={() => saveChallenge()}
                startIcon={<Save />}
              >
                Speichern
              </Button>
              <Button
                sx={{ mt: 3 }}
                variant={"contained"}
                color={"warning"}
                onClick={() => resetChallenge()}
                startIcon={<Cancel />}
              >
                Abbrechen
              </Button>
              {game?.challenges.find(({ id }) => id === challenge?.id) && (
                <Button
                  sx={{ mt: 3 }}
                  variant={"contained"}
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => removeChallenge()}
                >
                  Löschen
                </Button>
              )}
            </Box>
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
};

export default EditChallenge;
